import { X } from 'lucide-react'
import { useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Stepper, StepperStep } from '@/components/ui/stepper'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'
import { useCreatePlan } from '@/hooks/usePlanCreation'
import { toast } from '@/stores/useToastStore'
import type { CreatePlanPayload } from '@/api/planCreation'
import { MethodSelectionStep } from './MethodSelectionStep'
import { SourceSelectionStep } from './SourceSelectionStep'
import { HistoricalDataSourceStep } from './HistoricalDataSourceStep'
import { NameYourPlanStep } from './NameYourPlanStep'
import { WizardNavigation } from './WizardNavigation'

// Step configurations per method
const STEP_CONFIGS = {
  blank: {
    steps: [
      { id: 'method', label: 'Method' },
      { id: 'name', label: 'Name' },
    ] as StepperStep[],
    lastStep: 1,
  },
  copy: {
    steps: [
      { id: 'method', label: 'Method' },
      { id: 'source', label: 'Source' },
      { id: 'name', label: 'Name' },
    ] as StepperStep[],
    lastStep: 2,
  },
  historicalData: {
    steps: [
      { id: 'method', label: 'Method' },
      { id: 'source', label: 'Source' },
      { id: 'name', label: 'Name' },
    ] as StepperStep[],
    lastStep: 2,
  },
}

// Validation state type for step validators
interface ValidationState {
  selectedMethod: string | null
  selectedDepartmentId: string | null
  selectedClassId: string | null
  planName: string
  nameValidationStatus: string
  selectedSourcePlan: { id: string } | null
  historicalDataSourceType: 'plan' | 'file' | null
  historicalSourcePlan: { id: string } | null
  historicalDataItems: unknown[] | null
  hasBlockingMismatches: boolean
}

// Config-driven step validators - returns true if step is valid
const STEP_VALIDATORS: Record<
  string,
  Record<number, (state: ValidationState) => boolean>
> = {
  blank: {
    1: s => s.planName.trim() !== '' && s.nameValidationStatus === 'valid',
  },
  copy: {
    1: s => s.selectedSourcePlan !== null,
    2: s => s.planName.trim() !== '' && s.nameValidationStatus === 'valid',
  },
  historicalData: {
    1: s => {
      if (s.historicalDataSourceType === 'plan') {
        return s.historicalSourcePlan !== null
      }
      if (s.historicalDataSourceType === 'file') {
        return (
          s.historicalDataItems !== null &&
          s.historicalDataItems.length > 0 &&
          !s.hasBlockingMismatches
        )
      }
      return false
    },
    2: s => s.planName.trim() !== '' && s.nameValidationStatus === 'valid',
  },
}

export function PlanWizard() {
  const navigate = useNavigate()
  const createPlanMutation = useCreatePlan()

  const {
    isOpen,
    currentStep,
    selectedMethod,
    selectedDepartmentId,
    selectedClassId,
    selectedHierarchyId,
    planName,
    nameValidationStatus,
    selectedSourcePlan,
    sourceVersionId,
    historicalDataItems,
    historicalDataSourceType,
    uploadedFileName,
    historicalSourcePlan,
    productListId,
    hasBlockingMismatches,
    closeWizard,
    nextStep,
    prevStep,
    resetWizard,
    clearSourceSelection,
    resetHistoricalDataSource,
    setPlanName,
  } = usePlanWizardStore()

  // Get current step configuration
  const stepConfig = useMemo(() => {
    if (!selectedMethod) return STEP_CONFIGS.blank
    return STEP_CONFIGS[selectedMethod] || STEP_CONFIGS.blank
  }, [selectedMethod])

  const wizardSteps = stepConfig.steps
  const isLastStep = currentStep === stepConfig.lastStep

  const handleClose = () => {
    closeWizard()
    setTimeout(() => resetWizard(), 300)
  }

  const handleContinue = useCallback(() => {
    nextStep()
  }, [nextStep])

  const handleBack = useCallback(() => {
    // Clear source selection when going back from step 1 in copy flow
    if (currentStep === 1 && selectedMethod === 'copy') {
      clearSourceSelection()
    }
    // Clear source selection when going back from step 1 in historical data flow
    if (currentStep === 1 && selectedMethod === 'historicalData') {
      resetHistoricalDataSource()
    }

    // Clear plan name when going back from the name step
    const isNameStep =
      (selectedMethod === 'blank' && currentStep === 1) ||
      ((selectedMethod === 'copy' || selectedMethod === 'historicalData') &&
        currentStep === 2)
    if (isNameStep) {
      setPlanName('')
    }

    prevStep()
  }, [
    currentStep,
    prevStep,
    selectedMethod,
    clearSourceSelection,
    resetHistoricalDataSource,
    setPlanName,
  ])

  // Build base payload with hierarchy fields (shared across all methods)
  const getHierarchyPayload = useCallback(
    () => ({
      departmentId: selectedDepartmentId ?? undefined,
      classId: selectedClassId ?? undefined,
      hierarchyId: selectedHierarchyId ?? undefined,
    }),
    [selectedDepartmentId, selectedClassId, selectedHierarchyId]
  )

  // Handle form submission
  const handleSubmit = () => {
    if (!selectedMethod) return

    const basePayload: CreatePlanPayload = {
      method: selectedMethod,
      planName: planName.trim(),
      ...getHierarchyPayload(),
    }

    // Add method-specific fields
    let payload = basePayload

    if (selectedMethod === 'copy') {
      payload = {
        ...basePayload,
        sourcePlanId: selectedSourcePlan?.id ?? undefined,
        sourceVersionId: sourceVersionId ?? undefined,
      }
    }

    if (selectedMethod === 'historicalData') {
      payload = {
        ...basePayload,
        historicalSourceType: historicalDataSourceType ?? undefined,
        historicalDataItems: historicalDataItems ?? undefined,
        ...(historicalDataSourceType === 'plan' && {
          historicalSourcePlanId: historicalSourcePlan?.id ?? undefined,
          sourceVersionId: sourceVersionId ?? undefined,
        }),
        ...(historicalDataSourceType === 'file' && {
          uploadedFileName: uploadedFileName ?? undefined,
          productListId: productListId ?? undefined,
        }),
      }
    }

    createPlanMutation.mutate(payload, {
      onSuccess: () => {
        closeWizard()
        navigate('/')
        setTimeout(() => resetWizard(), 300)
      },
      onError: (error: Error) => {
        toast.error(`Failed to create plan: ${error.message}`)
      },
    })
  }

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Determine if continue button should be enabled using config-driven validators
  const canContinue = useMemo(() => {
    // Step 0: Method selection - all methods
    if (currentStep === 0) {
      return selectedMethod !== null
    }

    // Use config-driven validators for method-specific steps
    if (!selectedMethod) return false

    const validator = STEP_VALIDATORS[selectedMethod]?.[currentStep]
    if (!validator) return true

    const state: ValidationState = {
      selectedMethod,
      selectedDepartmentId,
      selectedClassId,
      planName,
      nameValidationStatus,
      selectedSourcePlan,
      historicalDataSourceType,
      historicalSourcePlan,
      historicalDataItems,
      hasBlockingMismatches,
    }

    return validator(state)
  }, [
    currentStep,
    selectedMethod,
    selectedDepartmentId,
    selectedClassId,
    planName,
    nameValidationStatus,
    selectedSourcePlan,
    historicalDataSourceType,
    historicalSourcePlan,
    historicalDataItems,
    hasBlockingMismatches,
  ])

  // Render current step component using config-driven approach
  const renderStep = () => {
    if (currentStep === 0) {
      return <MethodSelectionStep />
    }

    // Method-specific steps
    const stepComponents = {
      blank: {
        1: <NameYourPlanStep />,
      },
      copy: {
        1: <SourceSelectionStep />,
        2: <NameYourPlanStep />,
      },
      historicalData: {
        1: <HistoricalDataSourceStep />,
        2: <NameYourPlanStep />,
      },
    }

    if (!selectedMethod) return null
    const methodSteps = stepComponents[selectedMethod]
    const StepComponent = methodSteps?.[currentStep as keyof typeof methodSteps]

    if (StepComponent) {
      return StepComponent
    }

    return (
      <div className="py-12 text-center text-gray-500">
        Step {currentStep + 1} - Coming soon
        <p className="mt-2 text-sm">
          Selected method: <strong>{selectedMethod}</strong>
        </p>
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header - White background */}
      <div className="bg-white px-8 pt-4 pb-2 border-b border-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#1c1c1c]">Create New Plan</h1>
          <button
            onClick={handleClose}
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Stepper - White background */}
      <div className="bg-white px-8 py-6 border-b border-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]">
        <Stepper steps={wizardSteps} currentStep={currentStep} />
      </div>

      {/* Step Content - Light gray background */}
      <div className="flex-1 bg-[#f8f8f8] py-8 px-4 md:px-8 lg:px-16 2xl:px-[267.5px] overflow-y-auto min-h-0 max-w-full">
        {renderStep()}
      </div>

      {/* Navigation - White background */}
      <div className="bg-white px-8 py-[25px] border-t border-white shadow-[0px_-1px_3px_0px_rgba(0,0,0,0.1),0px_-1px_2px_-1px_rgba(0,0,0,0.1)]">
        <WizardNavigation
          onBack={handleBack}
          onContinue={handleContinue}
          onSubmit={handleSubmit}
          canGoBack={currentStep > 0 && !createPlanMutation.isPending}
          canContinue={canContinue}
          isLastStep={isLastStep}
          isSubmitting={createPlanMutation.isPending}
        />
      </div>
    </div>
  )
}
